import { create } from 'zustand';
import type { CustomerAccountRow, CustomerMovementRow } from '../domain/customerTypes';
import type { CustomerPaymentMethodId } from '../infra/customerPayments';

export type CustomerCollectIntent = {
  customerId: string;
  saleId: string;
  suggestedAmount?: number;
};

/** Draft de un cheque dentro de un cobro (multi-cheque Fase C). */
export type CustomerCheckDraft = {
  number: string;
  dueDate: string;
  drawer: string;
  bank: string;
  /** Monto del cheque (obligatorio solo cuando hay más de un cheque). */
  amount: string;
};

export const emptyCheckDraft = (): CustomerCheckDraft => ({
  number: '',
  dueDate: '',
  drawer: '',
  bank: '',
  amount: '',
});

type CustomerOverlayState = {
  collectIntent: CustomerCollectIntent | null;
  /** Abrir «Agregar cliente» al aterrizar en Clientes (p. ej. desde turno). */
  pendingOpenNewCustomer: boolean;
  /** Abrir la ficha de este customerId al aterrizar en Clientes (p. ej. desde detalle de turno). */
  pendingOpenCustomerDetailId: string | null;
  movementModal: boolean;
  selectedCustomer: CustomerAccountRow | null;
  movements: CustomerMovementRow[];
  movementLoading: boolean;
  movementsOffset: number;
  movementsHasMore: boolean;
  movementsLoadingMore: boolean;
  amountInput: string;
  noteInput: string;
  /** Medio de cobro elegido en el modal de pago (null = sin selección explícita). */
  paymentMethod: CustomerPaymentMethodId | null;
  checkDrafts: CustomerCheckDraft[];
  customerEditorVisible: boolean;
  editingCustomer: CustomerAccountRow | null;
  formName: string;
  formPhone: string;
  formCuit: string;
  formLimit: string;
  formEmail: string;
  formAddress: string;
  formTaxCategory: string;
  formNotes: string;
  formPaymentTermsDays: string;
  formPriceListId: string;
  setCollectIntent: (intent: CustomerCollectIntent) => void;
  consumeCollectIntent: () => CustomerCollectIntent | null;
  requestOpenNewCustomer: () => void;
  consumeOpenNewCustomerRequest: () => boolean;
  requestOpenCustomerDetail: (customerId: string) => void;
  consumeOpenCustomerDetailRequest: () => string | null;
  setMovementModal: (v: boolean) => void;
  setSelectedCustomer: (c: CustomerAccountRow | null) => void;
  setMovements: (m: CustomerMovementRow[] | ((prev: CustomerMovementRow[]) => CustomerMovementRow[])) => void;
  setMovementLoading: (v: boolean) => void;
  setMovementsOffset: (n: number | ((prev: number) => number)) => void;
  setMovementsHasMore: (v: boolean) => void;
  setMovementsLoadingMore: (v: boolean) => void;
  setAmountInput: (s: string) => void;
  setNoteInput: (s: string) => void;
  setPaymentMethod: (m: CustomerPaymentMethodId | null) => void;
  setCheckDrafts: (drafts: CustomerCheckDraft[]) => void;
  updateCheckDraft: (index: number, patch: Partial<CustomerCheckDraft>) => void;
  addCheckDraft: () => void;
  removeCheckDraft: (index: number) => void;
  setCustomerEditorVisible: (v: boolean) => void;
  setEditingCustomer: (c: CustomerAccountRow | null) => void;
  setFormName: (s: string) => void;
  setFormPhone: (s: string) => void;
  setFormCuit: (s: string) => void;
  setFormLimit: (s: string) => void;
  setFormEmail: (s: string) => void;
  setFormAddress: (s: string) => void;
  setFormTaxCategory: (s: string) => void;
  setFormNotes: (s: string) => void;
  setFormPaymentTermsDays: (s: string) => void;
  setFormPriceListId: (s: string) => void;
  closeMovementModal: () => void;
  resetAll: () => void;
};

const initial = () => ({
  collectIntent: null as CustomerCollectIntent | null,
  pendingOpenNewCustomer: false,
  pendingOpenCustomerDetailId: null,
  movementModal: false,
  selectedCustomer: null as CustomerAccountRow | null,
  movements: [] as CustomerMovementRow[],
  movementLoading: false,
  movementsOffset: 0,
  movementsHasMore: false,
  movementsLoadingMore: false,
  amountInput: '',
  noteInput: '',
  paymentMethod: null as CustomerPaymentMethodId | null,
  checkDrafts: [emptyCheckDraft()],
  customerEditorVisible: false,
  editingCustomer: null as CustomerAccountRow | null,
  formName: '',
  formPhone: '',
  formCuit: '',
  formLimit: '',
  formEmail: '',
  formAddress: '',
  formTaxCategory: 'consumidor_final',
  formNotes: '',
  formPaymentTermsDays: '',
  formPriceListId: '',
});

export const useCustomerOverlayStore = create<CustomerOverlayState>((set, get) => ({
  ...initial(),
  setCollectIntent: (intent) => set({ collectIntent: intent }),
  consumeCollectIntent: () => {
    const current = get().collectIntent;
    if (!current) return null;
    set({ collectIntent: null });
    return current;
  },
  requestOpenNewCustomer: () => set({ pendingOpenNewCustomer: true }),
  consumeOpenNewCustomerRequest: () => {
    if (!get().pendingOpenNewCustomer) return false;
    set({ pendingOpenNewCustomer: false });
    return true;
  },
  requestOpenCustomerDetail: (customerId) => set({ pendingOpenCustomerDetailId: customerId }),
  consumeOpenCustomerDetailRequest: () => {
    const current = get().pendingOpenCustomerDetailId;
    if (!current) return null;
    set({ pendingOpenCustomerDetailId: null });
    return current;
  },
  setMovementModal: (v) => set({ movementModal: v }),
  setSelectedCustomer: (c) => set({ selectedCustomer: c }),
  setMovements: (m) =>
    set((state) => ({
      movements: typeof m === 'function' ? m(state.movements) : m,
    })),
  setMovementLoading: (v) => set({ movementLoading: v }),
  setMovementsOffset: (n) =>
    set((state) => ({
      movementsOffset: typeof n === 'function' ? n(state.movementsOffset) : n,
    })),
  setMovementsHasMore: (v) => set({ movementsHasMore: v }),
  setMovementsLoadingMore: (v) => set({ movementsLoadingMore: v }),
  setAmountInput: (s) => set({ amountInput: s }),
  setNoteInput: (s) => set({ noteInput: s }),
  setPaymentMethod: (m) => set({ paymentMethod: m }),
  setCheckDrafts: (drafts) => set({ checkDrafts: drafts }),
  updateCheckDraft: (index, patch) =>
    set((state) => ({
      checkDrafts: state.checkDrafts.map((d, i) => (i === index ? { ...d, ...patch } : d)),
    })),
  addCheckDraft: () => set((state) => ({ checkDrafts: [...state.checkDrafts, emptyCheckDraft()] })),
  removeCheckDraft: (index) =>
    set((state) => ({
      checkDrafts:
        state.checkDrafts.length <= 1
          ? state.checkDrafts
          : state.checkDrafts.filter((_, i) => i !== index),
    })),
  setCustomerEditorVisible: (v) => set({ customerEditorVisible: v }),
  setEditingCustomer: (c) => set({ editingCustomer: c }),
  setFormName: (s) => set({ formName: s }),
  setFormPhone: (s) => set({ formPhone: s }),
  setFormCuit: (s) => set({ formCuit: s }),
  setFormLimit: (s) => set({ formLimit: s }),
  setFormEmail: (s) => set({ formEmail: s }),
  setFormAddress: (s) => set({ formAddress: s }),
  setFormTaxCategory: (s) => set({ formTaxCategory: s }),
  setFormNotes: (s) => set({ formNotes: s }),
  setFormPaymentTermsDays: (s) => set({ formPaymentTermsDays: s }),
  setFormPriceListId: (s) => set({ formPriceListId: s }),
  closeMovementModal: () =>
    set({
      movementModal: false,
      selectedCustomer: null,
      movements: [],
      movementsOffset: 0,
      movementsHasMore: false,
      amountInput: '',
      noteInput: '',
      paymentMethod: null,
      checkDrafts: [emptyCheckDraft()],
    }),
  resetAll: () => set(initial()),
}));

/** Cierra modales de cobro/edición al salir del tab Customer. */
export function resetCustomerTabOverlays() {
  useCustomerOverlayStore.getState().resetAll();
}
